import React, { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';

import { MailingClassStep } from '@/components/postgrid/mailing-class';
import {
  AttachmentSelectionStep,
  LetterReviewStep,
  Wizard,
  type WizardStepDefinition,
} from '@/components/postgrid/wizard';
import type { CreatePostgridLetter } from '@/services/api/generated/postgridService';
import {
  ExtractedRecipientInfo,
  ExtractRecipientResponse,
  usePostgridLettersExtractRecipientMutation,
} from '@/services/api/postgridService';

import { LetterContactsStep } from './LetterContactsStep';

/** Timeout for recipient extraction in milliseconds */
const EXTRACTION_TIMEOUT_MS = 10000;

interface LetterWizardProps {
  matterId: string;
  onSuccess: (data: CreatePostgridLetter) => void;
  onCancel?: () => void;
  isLoading?: boolean;
  // Prefill options for better UX
  prefilledToContactId?: string;
}

// Reordered steps: Attachments first to enable recipient extraction from uploaded documents
const letterWizardSteps: WizardStepDefinition[] = [
  {
    id: 'attachments',
    title: 'Attachments',
    description: 'Include supporting documents',
  },
  {
    id: 'recipient',
    title: 'Recipient',
    description: 'Select contact',
  },
  {
    id: 'mailing-class',
    title: 'Mailing Class',
    description: 'Delivery speed & extras',
  },
  {
    id: 'review',
    title: 'Review',
    description: 'Confirm & submit',
  },
];

export const LetterWizard: React.FC<LetterWizardProps> = ({
  matterId,
  onSuccess,
  onCancel,
  isLoading = false,
  prefilledToContactId,
}) => {
  // Generate a stable idempotency key for this wizard session
  const idempotencyKey = useMemo(
    () => `letter-${matterId}-${uuidv4()}`,
    [matterId],
  );

  // State for extracted recipient info from uploaded documents
  const [extractedRecipient, setExtractedRecipient] =
    useState<ExtractedRecipientInfo | null>(null);

  // Mutation hook for recipient extraction
  const [extractRecipient] = usePostgridLettersExtractRecipientMutation();

  // Callback to handle blob uploads and trigger extraction (non-blocking with timeout)
  const handleBlobUploaded = useCallback(
    (blobId: string) => {
      // Only extract if we haven't already extracted recipient info
      if (extractedRecipient) return;

      // Create a timeout promise that rejects after EXTRACTION_TIMEOUT_MS
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(
          () => reject(new Error('EXTRACTION_TIMEOUT')),
          EXTRACTION_TIMEOUT_MS,
        );
      });

      // Start extraction promise
      const extractionPromise = extractRecipient({
        extractRecipientRequest: { blob_id: blobId },
      }).unwrap();

      // Race between extraction and timeout, wrapped in toast.promise for progress UI
      toast.promise(
        Promise.race([extractionPromise, timeoutPromise]).then(
          (result: ExtractRecipientResponse) => {
            if (result.success && result.recipient) {
              setExtractedRecipient(result.recipient);
              return result;
            }
            throw new Error('EXTRACTION_FAILED');
          },
        ),
        {
          loading: 'Extracting recipient information...',
          success: (result: ExtractRecipientResponse) => {
            if (result.recipient && (result.recipient.confidence ?? 0) >= 0.5) {
              return 'Recipient information extracted. Review in next step.';
            }
            return 'Some recipient info extracted - verification needed.';
          },
          error: (err: Error) => {
            if (err.message === 'EXTRACTION_TIMEOUT') {
              return 'Extraction timed out - enter recipient manually';
            }
            // Silent fail for other errors - extraction is optional
            return 'Could not extract recipient - enter manually';
          },
        },
      );
    },
    [extractRecipient, extractedRecipient],
  );

  const handleComplete = async (wizardData: Record<string, any>) => {
    try {
      const payload: CreatePostgridLetter = {
        to_contact_id: wizardData.to_contact_id,
        from_contact_id: '', // No sender field in UI - backend will handle default
        matter_id: matterId,
        // Use stable idempotency key for this wizard session
        idempotency_key: idempotencyKey,
        // Backend will handle defaults
        double_sided: false,
        color: false,
        mailing_class: wizardData.mailing_class,
        return_envelope_id: null,
        blob_ids: wizardData.attachment_blob_ids ?? [],
        use_return_envelope: wizardData.use_return_envelope,
      };

      onSuccess(payload);
      toast.success('Letter created successfully!');
    } catch (error) {
      console.error('Failed to create letter:', error);
      toast.error('Failed to create letter. Please try again.');
    }
  };

  // Prepare initial data with prefilled values
  const initialData = {
    ...(prefilledToContactId && { to_contact_id: prefilledToContactId }),
    use_return_envelope: false, // Initialize with default value
  };

  return (
    <Wizard
      title="Send Mail"
      description="Follow these steps to compose and send a professional letter."
      steps={letterWizardSteps}
      onComplete={handleComplete}
      onCancel={onCancel}
      isLoading={isLoading}
      initialData={initialData}
    >
      {/* Step 1: Attachments - upload documents first to enable recipient extraction */}
      <AttachmentSelectionStep
        matterId={matterId}
        onBlobUploaded={handleBlobUploaded}
      />
      {/* Step 2: Recipient - pre-populated with extracted info if available */}
      <LetterContactsStep extractedRecipient={extractedRecipient} />
      {/* Step 3: Mailing Class */}
      <MailingClassStep />
      {/* Step 4: Review */}
      <LetterReviewStep matterId={matterId} />
    </Wizard>
  );
};
